import React from 'react';
import {
  Box,
  Autocomplete,
  TextField,
  Chip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

export interface OperatorOption {
  targetDO: string;
}

interface OperatorFilterProps {
  /** All available operators for the current year (Settings-permitted, visible). */
  availableOperators: OperatorOption[];
  /** Currently selected operators. Empty array = show all. */
  selectedOperators: OperatorOption[];
  /** Called when the selection changes. */
  onChange: (selected: OperatorOption[]) => void;
  disabled?: boolean;
}

/**
 * Multi-select autocomplete to filter which counterpart operators
 * are displayed in the UserModuleMatrix.
 */
const OperatorFilter: React.FC<OperatorFilterProps> = ({
  availableOperators,
  selectedOperators,
  onChange,
  disabled = false,
}) => {
  const { t } = useTranslation();

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ mb: 1, fontWeight: 'bold' }}>
        {t('userPreferences.operatorFilter.label', 'Counterpart Operators')}
      </Box>
      <Autocomplete
        multiple
        options={availableOperators}
        value={selectedOperators}
        onChange={(_event, newValue) => onChange(newValue)}
        getOptionLabel={(option) => option.targetDO}
        isOptionEqualToValue={(option, value) => option.targetDO === value.targetDO}
        disabled={disabled}
        size="small"
        disableCloseOnSelect
        renderTags={(value, getTagProps) =>
          value.map((option, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            return (
              <Chip
                key={key}
                label={option.targetDO}
                size="small"
                {...tagProps}
              />
            );
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            placeholder={
              selectedOperators.length === 0
                ? t('userPreferences.operatorFilter.placeholder', 'All operators (no filter)')
                : ''
            }
          />
        )}
        sx={{ maxWidth: 500 }}
        data-testid="user-preferences-operator-filter"
      />
    </Box>
  );
};

export default OperatorFilter;

